import { FormlyFieldConfig } from '@ngx-formly/core';
import { ReportFieldType, ReportTimeRange } from '../models/report.model';

/**
 * Pure helpers shared by the report builder and query engine:
 * - mapping form (Formly) field types to reporting value types
 * - enumerating a form's reportable dynamic fields
 * - resolving a time-range preset to a concrete epoch-millis window
 *
 * These are intentionally framework-free (no Angular DI) so they can be unit
 * tested in isolation.
 */

/**
 * Maps a Formly field `type` to the reporting value type used for operator
 * selection, coercion, and formatting. Returns `null` for field types that are
 * not meaningfully reportable (photos, QR codes, decorative labels).
 */
export function mapFormlyTypeToReportFieldType(field: FormlyFieldConfig): ReportFieldType | null {
  const type = (field.type as string) ?? 'text';

  switch (type) {
    case 'number':
    case 'duration':
    case 'calculated': // calculated outputs persist as numeric scalars (design §6.4)
      return 'number';

    case 'date':
    case 'datetime':
    case 'time':
      return 'date';

    case 'checkbox':
      return 'boolean';

    case 'radio':
    case 'dropdown':
    case 'select':
    case 'checkbox-group':
      return 'enum';

    case 'text':
    case 'textarea':
    case 'email':
    case 'tel':
      return 'text';

    // Not reportable
    case 'photo':
    case 'qr-code':
    case 'text-label':
      return null;

    default:
      // Unknown/custom types default to text so they remain filterable/selectable.
      return 'text';
  }
}

/** A dynamic (form-specific) field exposed to the report builder. */
export interface ReportableDynamicField {
  /** Key within interaction_records.data JSON. */
  key: string;
  label: string;
  fieldType: ReportFieldType;
  /** Present for enum fields — the selectable option values and labels. */
  options?: { label: string; value: string | number }[];
}

/**
 * Enumerates the reportable dynamic fields of a form definition, skipping
 * non-reportable field types and fields without a stable key.
 */
export function getReportableDynamicFields(
  fields: FormlyFieldConfig[] | null | undefined,
): ReportableDynamicField[] {
  if (!fields) return [];

  const result: ReportableDynamicField[] = [];
  for (const field of fields) {
    const key = (field.key as string) ?? undefined;
    if (!key) continue;

    const fieldType = mapFormlyTypeToReportFieldType(field);
    if (!fieldType) continue;

    const label = (field.props?.label as string) ?? key;
    const rawOptions = field.props?.['options'] as
      | { label: string; value: string | number }[]
      | undefined;

    result.push({
      key,
      label,
      fieldType,
      options: fieldType === 'enum' && Array.isArray(rawOptions) ? rawOptions : undefined,
    });
  }

  return result;
}

/** Concrete epoch-millis window. `null` bounds mean "unbounded" on that side. */
export interface ResolvedTimeRange {
  startMs: number | null;
  endMs: number | null;
}

/**
 * Resolves a {@link ReportTimeRange} to a concrete epoch-millis window.
 *
 * Relative presets are computed against `now` (injectable for testing). Weeks
 * start on Monday. The end bound is exclusive-friendly: it is the last
 * millisecond of the period so callers can use `created_at <= endMs`.
 */
export function resolveTimeRange(
  range: ReportTimeRange | undefined,
  now: Date = new Date(),
): ResolvedTimeRange {
  if (!range || range.preset === 'all_time') {
    return { startMs: null, endMs: null };
  }

  if (range.preset === 'custom') {
    return { startMs: range.startMs ?? null, endMs: range.endMs ?? null };
  }

  const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const endOfDay = (d: Date): Date =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  const addDays = (d: Date, n: number): Date => {
    const copy = new Date(d);
    copy.setDate(copy.getDate() + n);
    return copy;
  };
  // Monday=0 ... Sunday=6
  const isoWeekdayIndex = (d: Date): number => (d.getDay() + 6) % 7;

  switch (range.preset) {
    case 'today':
      return { startMs: startOfDay(now).getTime(), endMs: endOfDay(now).getTime() };

    case 'yesterday': {
      const y = addDays(now, -1);
      return { startMs: startOfDay(y).getTime(), endMs: endOfDay(y).getTime() };
    }

    case 'this_week': {
      const start = startOfDay(addDays(now, -isoWeekdayIndex(now)));
      return { startMs: start.getTime(), endMs: endOfDay(now).getTime() };
    }

    case 'last_week': {
      const thisWeekStart = startOfDay(addDays(now, -isoWeekdayIndex(now)));
      const lastWeekStart = addDays(thisWeekStart, -7);
      const lastWeekEnd = endOfDay(addDays(thisWeekStart, -1));
      return { startMs: lastWeekStart.getTime(), endMs: lastWeekEnd.getTime() };
    }

    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startMs: start.getTime(), endMs: endOfDay(now).getTime() };
    }

    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { startMs: start.getTime(), endMs: end.getTime() };
    }

    case 'last_7_days': {
      const start = startOfDay(addDays(now, -6));
      return { startMs: start.getTime(), endMs: endOfDay(now).getTime() };
    }

    case 'last_30_days': {
      const start = startOfDay(addDays(now, -29));
      return { startMs: start.getTime(), endMs: endOfDay(now).getTime() };
    }

    default:
      return { startMs: null, endMs: null };
  }
}
